import { ResizeSensor2 as BlueprintResizeSensor2 } from '@blueprintjs/popover2';
import classNames from 'classnames';
import * as React from 'react';

import { VoiceboxChatMessageDivider } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatMessageDivider';
import { VoiceboxChatMessageLoading } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatMessageLoading';
import { VoiceboxChatMessageRequest } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatMessageRequest';
import { VoiceboxChatMessageResponse } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatMessageResponse';
import { VoiceboxChatSettingsDivider } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatSettingsDivider';
import { VoiceboxChatSuggestedPrompts } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatSuggestedPrompts';
import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { VoiceboxChatTextarea } from 'bones/components/molecules/Voicebox/shared/VoiceboxChatTextarea';
import { VoiceboxDescription } from 'bones/components/molecules/Voicebox/shared/VoiceboxDescription';
import {
  VoiceboxApp,
  VoiceboxMessage,
  VoiceboxRequestMessage,
} from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { isEqual, isNotEqual } from 'bones/utils/isEqual';
import { getSettingsFromRequestMessage } from 'bones/utils/voicebox/getSettingsFromRequestMessage';

export interface VoiceboxChatProps {
  aiReasoning: boolean;
  lastTypedQuery: string;
  indicator: React.ReactNode;
  isReasoningOpen: (id: string | undefined) => boolean;
  query: string;
  queryHistoryIdx: number;
  sendMessage: (message: string) => void;
  sendMessageDisabled: boolean;
  setAiReasoning: (aiReasoning: boolean) => void;
  setIsReasoningOpen: (id: string | undefined) => (isOpen: boolean) => void;
  setLastTypedQuery: (query: string) => void;
  setQuery: (query: string) => void;
  setQueryHistoryIdx: (queryHistoryIdx: number) => void;
}

export const VoiceboxChat: React.VFC<VoiceboxChatProps> = ({
  aiReasoning,
  indicator,
  lastTypedQuery,
  isReasoningOpen,
  query,
  queryHistoryIdx,
  sendMessage,
  sendMessageDisabled,
  setAiReasoning,
  setIsReasoningOpen,
  setLastTypedQuery,
  setQuery,
  setQueryHistoryIdx,
}) => {
  const { config, testIds, links } = useVoiceboxSchema();
  const { isLoadingMessage, messages } = useVoiceboxConversation();
  const { currentPanel } = useVoiceboxPanelData();
  const {
    databasesLoading,
    namedGraphsLoading,
    settings,
    lastSettingsLoading,
    lastSettingsRefetching,
  } = useVoiceboxSettings();
  const [isOpen, setIsOpen] = React.useState(false);

  const isDesigner = config.app === VoiceboxApp.DESIGNER;

  const lastSettingsFetching = lastSettingsLoading || lastSettingsRefetching;
  const allSettingsLoading =
    databasesLoading || namedGraphsLoading || lastSettingsFetching;
  const didAllSettingsLoadingChange = useDidValueChange(allSettingsLoading);
  const didLoadingMessageChange = useDidValueChange(isLoadingMessage);
  const didLoadingMessageStart = isLoadingMessage && didLoadingMessageChange;
  const didMessagesLengthChange = useDidValueChange(messages.length);
  const didSettingsChange = useDidValueChange(settings, isEqual);

  const needsScroll =
    didLoadingMessageStart ||
    didAllSettingsLoadingChange ||
    didMessagesLengthChange ||
    didSettingsChange;

  const messagesRef = React.useRef<HTMLDivElement>(null);
  const lastScrollPercentageRef = React.useRef<number>(0);
  const ignoreScrollRef = React.useRef<boolean>(false);
  React.useEffect(() => {
    const messagesDiv = messagesRef.current;
    if (messagesDiv && needsScroll) {
      // scroll to bottom of messages
      messagesDiv.scrollTop = messagesDiv.scrollHeight;

      // set the current scroll percentage
      lastScrollPercentageRef.current =
        messagesDiv.scrollTop /
        (messagesDiv.scrollHeight - messagesDiv.clientHeight);
    }
  }, [needsScroll]);

  const handleResize = React.useCallback(() => {
    const messagesDiv = messagesRef.current;

    // ignore any scroll events from the resize
    ignoreScrollRef.current = true;

    // istanbul ignore else
    if (
      messagesDiv &&
      (Number.isNaN(lastScrollPercentageRef.current) || // no scroll
        lastScrollPercentageRef.current === 1) // full scroll
    ) {
      // keep scroll at the bottom of messages on resize
      messagesDiv.scrollTop = messagesDiv.scrollHeight;
    }
  }, []);

  const handleScroll = React.useCallback(() => {
    const messagesDiv = messagesRef.current;

    // ignore any scroll events from a resize
    if (messagesDiv && !ignoreScrollRef.current) {
      // set the current scroll percentage
      lastScrollPercentageRef.current =
        messagesDiv.scrollTop /
        (messagesDiv.scrollHeight - messagesDiv.clientHeight);
    }

    // reset listening to scroll events
    ignoreScrollRef.current = false;
  }, []);

  let lastMessage: VoiceboxMessage | undefined;
  let lastRequestMessage: VoiceboxRequestMessage | undefined;
  const settingsGroups = messages.reduce<
    Array<{
      settings: VoiceboxSettings;
      messages: Array<{
        message: VoiceboxMessage;
        messageIdx: number;
      }>;
    }>
  >((acc, message, messageIdx) => {
    lastMessage = message;
    if (message.isRequest) {
      lastRequestMessage = message;
    }

    const prevGroup = acc[acc.length - 1];
    const currSettings = message.isRequest
      ? getSettingsFromRequestMessage(message, config.defaultSettings)
      : null;
    if (
      prevGroup &&
      (!message.isRequest || isEqual(prevGroup.settings, currSettings))
    ) {
      prevGroup.messages.push({ message, messageIdx });
    } else {
      acc.push({
        settings: currSettings || config.defaultSettings,
        messages: [{ message, messageIdx }],
      });
    }
    return acc;
  }, []);
  const lastResponseMessage = lastMessage?.isResponse ? lastMessage : undefined;

  const lastRequestMessageSettings =
    lastRequestMessage &&
    getSettingsFromRequestMessage(lastRequestMessage, {
      databaseId: '',
      model: '',
      namedGraphs: [],
      reasoning: true,
    });

  const showLastMessageDatabaseChanges =
    !isDesigner &&
    lastRequestMessageSettings &&
    lastRequestMessageSettings.databaseId !== settings.databaseId;

  const showLastMessageDescription =
    !isDesigner &&
    lastRequestMessageSettings &&
    (lastRequestMessageSettings.databaseId !== settings.databaseId ||
      lastRequestMessageSettings.model !== settings.model);

  const showLastMessageSettingsDivider =
    !isDesigner &&
    lastRequestMessageSettings &&
    isNotEqual(lastRequestMessageSettings, settings);

  const lastMessageDescription = showLastMessageDescription ? (
    <VoiceboxDescription
      databaseId={settings.databaseId}
      model={settings.model}
      size="sm"
    />
  ) : null;

  return (
    <div
      className={classNames('vb-voicebox-chat', {
        'vb-voicebox-chat--empty': !messages.length,
      })}
      data-testid={testIds.drawer.body}
    >
      <BlueprintResizeSensor2 onResize={handleResize}>
        <div className="vb-voicebox-chat-resize">
          <div
            ref={messagesRef}
            className="vb-voicebox-chat-messages"
            onScroll={handleScroll}
          >
            {indicator}
            {settingsGroups.map((group, groupIdx) => {
              const prevGroup = settingsGroups[groupIdx - 1];
              const showSettings = !isDesigner;
              const showDescription =
                (!isDesigner || !groupIdx) &&
                (!prevGroup ||
                  prevGroup.settings.databaseId !== group.settings.databaseId ||
                  prevGroup.settings.model !== group.settings.model);

              const groupDescription = showDescription ? (
                <VoiceboxDescription
                  databaseId={group.settings.databaseId}
                  model={group.settings.model}
                  size="sm"
                />
              ) : null;

              return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={groupIdx}>
                  {showSettings ? (
                    <VoiceboxChatSettingsDivider
                      isInitial={!groupIdx}
                      messagesRef={messagesRef}
                      prevSettings={prevGroup?.settings}
                      settings={group.settings}
                    />
                  ) : null}
                  {groupDescription ? (
                    <div className="vb-voicebox-chat-indicator">
                      {groupDescription}
                    </div>
                  ) : null}
                  {group.messages.map(({ message, messageIdx }) => {
                    if (message.isDivider) {
                      return (
                        <VoiceboxChatMessageDivider
                          key={
                            message.messageId ||
                            /* istanbul ignore next */ messageIdx
                          }
                          message={message}
                        />
                      );
                    }

                    if (message.isRequest) {
                      return (
                        <VoiceboxChatMessageRequest
                          key={
                            message.messageId ||
                            /* istanbul ignore next */ messageIdx
                          }
                          message={message}
                          messageIdx={messageIdx}
                        />
                      );
                    }

                    if (message.pending) {
                      return null;
                    }

                    return (
                      <VoiceboxChatMessageResponse
                        key={
                          message.messageId ||
                          /* istanbul ignore next */ messageIdx
                        }
                        isReasoningOpen={isReasoningOpen(message.messageId)}
                        message={message}
                        messageIdx={messageIdx}
                        messagesRef={messagesRef}
                        selected={
                          links.openLinksInPanel
                            ? currentPanel?.messageId === message.messageId
                            : !showLastMessageDatabaseChanges &&
                              messageIdx === messages.length - 1
                        }
                        sendMessage={sendMessage}
                        sendMessageDisabled={sendMessageDisabled}
                        setIsReasoningOpen={setIsReasoningOpen(
                          message.messageId
                        )}
                        settings={group.settings}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
            {showLastMessageSettingsDivider ? (
              <VoiceboxChatSettingsDivider
                isInitial={!settingsGroups.length}
                messagesRef={messagesRef}
                prevSettings={lastRequestMessageSettings}
                settings={settings}
              />
            ) : null}
            {lastMessageDescription ? (
              <div className="vb-voicebox-chat-indicator">
                {lastMessageDescription}
              </div>
            ) : null}
            {isLoadingMessage ? (
              <VoiceboxChatMessageLoading
                isReasoningOpen={isReasoningOpen(
                  lastResponseMessage?.messageId
                )}
                lastResponseMessage={lastResponseMessage}
                setIsReasoningOpen={setIsReasoningOpen(
                  lastResponseMessage?.messageId
                )}
                settings={settings}
              />
            ) : null}
            <div className="vb-voicebox-chat-indicator" />
          </div>
        </div>
      </BlueprintResizeSensor2>

      <div className="vb-voicebox-chat-footer">
        <div className="vb-voicebox-chat-indicator">
          <VoiceboxChatSuggestedPrompts
            isOpen={isOpen}
            sendMessage={sendMessage}
            sendMessageDisabled={sendMessageDisabled}
            setIsOpen={setIsOpen}
          >
            <VoiceboxChatTextarea
              aiReasoning={aiReasoning}
              lastTypedQuery={lastTypedQuery}
              query={query}
              queryHistoryIdx={queryHistoryIdx}
              sendMessage={sendMessage}
              sendMessageDisabled={sendMessageDisabled}
              setAiReasoning={setAiReasoning}
              setIsSuggestedPromptsOpen={setIsOpen}
              setLastTypedQuery={setLastTypedQuery}
              setQuery={setQuery}
              setQueryHistoryIdx={setQueryHistoryIdx}
            />
          </VoiceboxChatSuggestedPrompts>
        </div>
      </div>
    </div>
  );
};
